import { apiClient } from '@/lib/api'

export type PlatformPlanQuotas = {
  storage_quota_mb: number | null
  max_orders_per_month: number | null
  max_products_total: number | null
  max_new_products_per_month: number | null
  max_customers_total: number | null
  max_new_customers_per_month: number | null
  max_inventory_locations?: number | null
  max_branches?: number | null
  max_staff?: number | null
  max_website_ai_generates_per_month?: number | null
  max_website_ai_rewrites_per_month?: number | null
  max_social_promote_ai_per_month?: number | null
}

export type PlatformPlan = {
  code: string
  name: string
  tagline: string
  price_label: string
  original_monthly_amount_cents?: number | null
  promo_badge?: string | null
  amount_cents: number | null
  currency: string
  interval: string
  self_serve: boolean
  contact_sales: boolean
  featured?: boolean
  points: string[]
  quotas?: PlatformPlanQuotas
}

export type SaasPaymentMethod = {
  provider: 'stripe' | 'kpay' | 'demopay' | 'airwallex' | string
  display_name: string
  environment: string
  is_default: boolean
}

export type ShopBillingStatus = {
  plan_code: string
  subscription_tier: string | null
  trial_ends_at: string | null
  trial_expired: boolean
  trial_days_remaining: number | null
  subscription_active: boolean
  /** When false, staff may view but not create/amend. */
  writes_allowed?: boolean
  /** Live Xero OAuth sync — paid subscription only. */
  xero_live_sync_allowed?: boolean
  features?: string[]
  subscription: {
    plan_code: string
    status: string
    billing_interval?: string
    current_period_end: string | null
  } | null
  stripe_configured: boolean
  gateways_configured?: boolean
  gateway_setup_hint?: string | null
  payment_methods?: SaasPaymentMethod[]
  active_membership_discount_rules_count?: number
  non_default_membership_customers_count?: number
  active_coupon_templates_count?: number
  active_companion_bundle_rules_count?: number
  active_shop_referrers_count?: number
  active_points_rewards_count?: number
}

export const platformBillingService = {
  async listPlans(): Promise<{
    success: boolean
    data?: {
      plans: PlatformPlan[]
      stripe_configured: boolean
      gateways_configured?: boolean
      gateway_setup_hint?: string | null
      payment_methods?: SaasPaymentMethod[]
    }
  }> {
    return apiClient.get('/platform/plans')
  },

  async getBilling(shopId: number): Promise<{ success: boolean; data?: ShopBillingStatus }> {
    return apiClient.get(`/platform/shops/${shopId}/billing`)
  },

  async startCheckout(
    shopId: number,
    planCode: string,
    provider?: string | null,
  ): Promise<{
    success: boolean
    message?: string
    data?: {
      checkout_url: string
      session_id: string
      mode: 'platform_shop_gateway' | 'stripe'
      order_id?: number
      payment_intent_id?: number
      provider?: string
    }
  }> {
    return apiClient.post(`/platform/shops/${shopId}/billing/checkout`, {
      plan_code: planCode,
      ...(provider ? { provider } : {}),
    })
  },

  async confirmCheckout(
    shopId: number,
    opts: {
      sessionId?: string | null
      orderId?: number | null
      /** Gateway redirect query params (KPay / Airwallex extras). */
      returnQuery?: Record<string, unknown>
    } = {},
  ): Promise<{
    success: boolean
    message?: string
    activated?: boolean
    payment_status?: string | null
    gateway?: string | null
    data?: ShopBillingStatus
  }> {
    return apiClient.post(`/platform/shops/${shopId}/billing/confirm`, {
      session_id: opts.sessionId || undefined,
      order_id: opts.orderId || undefined,
      ...(opts.returnQuery && Object.keys(opts.returnQuery).length > 0
        ? { return_query: opts.returnQuery }
        : {}),
    })
  },
}
